import { lazy, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import { SharedRecentSales } from '../../shared/SharedRecentSales/SharedRecentSales.tsx';
import { BuySharedPersonalizedSearchItems } from '../BuySharedPersonalizedSearchItems/BuySharedPersonalizedSearchItems.tsx';
import { type BuySharedAsyncModules_viewer$key } from './__generated__/BuySharedAsyncModules_viewer.graphql.ts';
import { type BuySharedAsyncModules_buyPage$key } from './__generated__/BuySharedAsyncModules_buyPage.graphql.ts';
import { type BuySharedAsyncModules_user$key } from './__generated__/BuySharedAsyncModules_user.graphql.ts';

const SharedYMALLazyComponent = lazy(
    () => import(/* webpackChunkName: "SharedYMAL" */ '../../shared/SharedYMAL/SharedYMAL.tsx')
);

const viewerFragment = graphql`
    fragment BuySharedAsyncModules_viewer on Viewer {
        buyPageSoldResults(uriRef: $uriRef) @include(if: $afterDisplayOrBtSsr) {
            ...SharedRecentSales_buyPageSoldResults
        }
        buyPageItemRecommendations(
            uriRef: $uriRef
            topItemIds: $topItemIds
            categoryLevel: $categoryLevel
        ) @include(if: $isClient) {
            ...SharedYMAL_buyPageItemRecommendations
            ...BuySharedPersonalizedSearchItems_buyPageItemRecommendations
        }
        ...SharedRecentSales_viewer
        ...SharedYMAL_viewer
        ...BuySharedPersonalizedSearchItems_viewer
    }
`;

const buyPageFragment = graphql`
    fragment BuySharedAsyncModules_buyPage on ItemSearchQueryConnection {
        ...SharedRecentSales_itemSearch
        ...SharedYMAL_itemSearch
        ...BuySharedPersonalizedSearchItems_itemSearch
    }
`;

const userFragment = graphql`
    fragment BuySharedAsyncModules_user on User {
        ...SharedRecentSales_user
        ...SharedYMAL_user
        ...BuySharedPersonalizedSearchItems_user
    }
`;

type Props = {
    viewer: BuySharedAsyncModules_viewer$key;
    buyPage: BuySharedAsyncModules_buyPage$key;
    user: BuySharedAsyncModules_user$key;
    isMobile?: boolean;
};

export const BuySharedAsyncModules: FC<Props> = ({
    viewer: viewerRef,
    buyPage: buyPageRef,
    user: userRef,
    isMobile = false,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const buyPage = useFragment(buyPageFragment, buyPageRef);
    const user = useFragment(userFragment, userRef);
    const { buyPageSoldResults, buyPageItemRecommendations } = viewer;
    return (
        <>
            <SharedRecentSales
                isMobile={isMobile}
                viewer={viewer}
                itemSearch={buyPage}
                buyPageSoldResults={buyPageSoldResults}
                user={user}
                hideRecentSoldPrice={!user}
            />
            <ClientSuspense fallback="">
                <SharedYMALLazyComponent
                    viewer={viewer}
                    itemSearch={buyPage}
                    buyPageItemRecommendations={buyPageItemRecommendations}
                    user={user}
                />
            </ClientSuspense>
            {/* on ssr_buy_page_modules cleanup: if success then move BuySharedPersonalizedSearchItems to ClientSuspense and make it lazy loaded */}
            <BuySharedPersonalizedSearchItems
                isMobile={isMobile}
                viewer={viewer}
                user={user}
                itemSearch={buyPage}
                buyPageItemRecommendations={buyPageItemRecommendations}
            />
        </>
    );
};
